import { fileExtension } from '../hooks/useFileHandler'
import { whatsappVariables } from './templateForm'

/**
 * Rules for WhatsApp templates that do not need React: the header, footer,
 * body and button limits WhatsApp sets, the media a header can carry, the
 * checks run before a template is sent for review, the
 * `meta_template_data` the API registers, and how a saved template's parts
 * are read back for a preview.
 *
 * Templates register through AWS End User Messaging (services
 * CommunicationManagementService `utils/aws_social_messaging_util.py`) and
 * send through lambda-comms-internal-trigger `util/aws_client.py`; the shapes
 * here are what those two read.
 */

// WhatsApp's own limits for a message template.
export const WA_LIMITS = {
    body: 1024,
    headerText: 60,
    footerText: 60,
    buttonText: 25,
    url: 2000,
    buttons: 10,
    urlButtons: 2,
    phoneButtons: 1,
}

const MB = 1024 * 1024

/**
 * The media a header can carry, keyed by the header type the form offers.
 *
 * `mediaType` is what the API stores in `meta_template_data.header`: the
 * registration and the send path both read a document as `PDF` and turn it
 * into WhatsApp's DOCUMENT. Images keep WhatsApp's 5 MB limit; videos and
 * documents stop at 16 MB because the production and dev ingress for the
 * comms API refuses request bodies over 20 MB. The extension is required as
 * well as the type: the registration repairs a missing content type from the
 * file's extension and falls back to JPEG without one.
 */
export const WA_MEDIA = {
    IMAGE: {
        label: 'image',
        mediaType: 'IMAGE',
        types: ['image/jpeg', 'image/png'],
        extensions: ['jpg', 'jpeg', 'png'],
        maxBytes: 5 * MB,
        kind: 'a JPEG or PNG image (.jpg or .png)',
    },
    VIDEO: {
        label: 'video',
        mediaType: 'VIDEO',
        types: ['video/mp4', 'video/3gpp'],
        extensions: ['mp4', '3gp', '3gpp'],
        maxBytes: 16 * MB,
        kind: 'an MP4 or 3GP video (.mp4 or .3gp)',
    },
    DOCUMENT: {
        label: 'document',
        mediaType: 'PDF',
        types: ['application/pdf'],
        extensions: ['pdf'],
        maxBytes: 16 * MB,
        kind: 'a PDF document (.pdf)',
    },
}

export const WA_HEADER_TYPES = [
    { id: 1, label: 'None', value: '' },
    { id: 2, label: 'Text', value: 'TEXT' },
    { id: 3, label: 'Image', value: 'IMAGE' },
    { id: 4, label: 'Video', value: 'VIDEO' },
    { id: 5, label: 'Document', value: 'DOCUMENT' },
]

/** True for a header type that carries a file (image, video, document). */
export const isMediaHeader = (headerType) =>
    Object.prototype.hasOwnProperty.call(WA_MEDIA, headerType ?? '')

/** The file picker's `accept` for a media header type ('' for none). */
export const mediaAccept = (headerType) =>
    isMediaHeader(headerType) ? WA_MEDIA[headerType].types.join(',') : ''

/** A byte count as megabytes with one decimal, for messages. */
const megabytes = (bytes) => `${(bytes / MB).toFixed(1)} MB`

/**
 * Why `file` cannot be the media of a `headerType` header, or '' when it can.
 *
 * The picker's `accept` only filters what it shows ("All files" still gets
 * through), so the type, the extension and the size are checked here, and
 * the message says what to choose instead.
 *
 * @param {string} headerType 'IMAGE' | 'VIDEO' | 'DOCUMENT'
 * @param {File} file the chosen file
 * @returns {string}
 */
export const mediaFileProblem = (headerType, file) => {
    const media = WA_MEDIA[headerType]
    if (!media || !file) return ''
    const typeFits = !file.type || media.types.includes(file.type)
    if (!typeFits || !media.extensions.includes(fileExtension(file.name)))
        return `${file.name} is not ${media.kind}. Choose ${media.kind} for this header.`
    if (!file.size) return `${file.name} is empty. Choose another file.`
    if (file.size > media.maxBytes)
        return `${file.name} is ${megabytes(file.size)}; a header ${
            media.label
        } can be at most ${media.maxBytes / MB} MB. Choose a smaller file.`
    return ''
}

/** A new, empty button row. */
export const emptyButton = () => ({
    button_type: '',
    button_text: '',
    button_data: '',
})

/**
 * The form's typed buttons as the AI chat reads them: `{type, text, url}` or
 * `{type, text, phone}` (a quick reply has neither).
 *
 * @param {object[]} buttons the form's rows
 * @returns {object[]}
 */
export const buttonsForAi = (buttons) =>
    whatsappButtons(buttons).map(
        ({ button_type: type, button_text: text, button_data: data }) =>
            type === 'URL'
                ? { type, text, url: data }
                : type === 'PHONE_NUMBER'
                ? { type, text, phone: data }
                : { type, text }
    )

/**
 * The AI chat's buttons as the form's rows. A link or number it could not
 * give is left empty, for the user to fill (the form says what is missing).
 * Two empty rows when there are none, as on a new template.
 *
 * @param {object[]} buttons the AI's `{type, text, url|phone}`
 * @returns {object[]}
 */
export const buttonRowsFromAi = (buttons) => {
    const rows = (buttons ?? []).map((b) => ({
        button_type: b.type,
        button_text: b.text ?? '',
        button_data:
            b.type === 'URL'
                ? b.url ?? ''
                : b.type === 'PHONE_NUMBER'
                ? b.phone ?? ''
                : '',
    }))
    return rows.length ? rows : [emptyButton(), emptyButton()]
}

/**
 * The WhatsApp parts of a new template's form, apart from the body and the
 * fields every channel has: header, footer, buttons and the example value
 * of each {{variable}}.
 */
export const emptyWhatsAppDraft = () => ({
    headerType: '',
    headerText: '',
    mediaFile: null,
    footerText: '',
    buttons: [emptyButton(), emptyButton()],
    examples: {},
})

const URL_RE = /^https?:\/\/\S+$/i
const PHONE_RE = /^\+\d{8,15}$/

/** A phone number as typed, without the spaces and hyphens people group digits with. */
const phoneNumber = (value) => String(value ?? '').replace(/[\s-]/g, '')

/**
 * The buttons as the API registers them: the rows with a type, each text and
 * link trimmed, a phone number without spaces or hyphens.
 *
 * @param {object[]} buttons the form's rows
 * @returns {{button_type: string, button_text: string, button_data: string}[]}
 */
export const whatsappButtons = (buttons) =>
    (buttons ?? [])
        .filter((b) => b?.button_type)
        .map((b) => ({
            button_type: b.button_type,
            button_text: String(b.button_text ?? '').trim(),
            button_data:
                b.button_type === 'PHONE_NUMBER'
                    ? phoneNumber(b.button_data)
                    : String(b.button_data ?? '').trim(),
        }))

/** What is wrong with one typed button, or '' (`n` is its 1-based row). */
const buttonRowProblem = (button, n) => {
    const text = String(button.button_text ?? '').trim()
    const data = String(button.button_data ?? '').trim()
    if (!text) return `Button ${n} needs its text.`
    if (text.length > WA_LIMITS.buttonText)
        return `Button ${n}: the text can be at most ${WA_LIMITS.buttonText} characters (it has ${text.length}).`
    if (button.button_type === 'URL') {
        if (!data) return `Button ${n} needs the link it opens.`
        if (!URL_RE.test(data))
            return `Button ${n}: the link must start with https:// and have no spaces.`
        if (data.length > WA_LIMITS.url)
            return `Button ${n}: the link can be at most ${WA_LIMITS.url} characters.`
    }
    if (button.button_type === 'PHONE_NUMBER') {
        const phone = phoneNumber(data)
        if (!phone) return `Button ${n} needs the phone number it calls.`
        if (!PHONE_RE.test(phone))
            return `Button ${n}: write the number as + and 8 to 15 digits with the country code, for example +919876543210.`
    }
    return ''
}

/**
 * Everything WhatsApp would refuse in the buttons, before the template is
 * sent for review.
 *
 * Each typed button needs text (at most 25 characters); a website button a
 * link that starts with https:// (or http://), has no spaces and is at most
 * 2000 characters; a phone button + and 8 to 15 digits. A template can have
 * at most 2 website buttons, 1 phone button and 10 buttons. AWS used to
 * refuse such a template at registration, after a button with an empty link
 * had been sent anyway.
 *
 * @param {object[]} buttons the form's rows
 * @returns {{rows: Object<number, string>, messages: string[]}} `rows` maps a
 *     row's index to what is wrong with it (to outline it); `messages` is
 *     every problem, in row order, for the banner
 */
export const buttonProblems = (buttons) => {
    const rows = {}
    const messages = []
    const typed = (buttons ?? [])
        .map((button, index) => ({ button, index }))
        .filter(({ button }) => button?.button_type)
    typed.forEach(({ button, index }) => {
        const problem = buttonRowProblem(button, index + 1)
        if (!problem) return
        rows[index] = problem
        messages.push(problem)
    })
    const tooMany = (type, limit, message) => {
        const ofType = typed.filter(({ button }) => button.button_type === type)
        if (ofType.length <= limit) return
        messages.push(message)
        ofType.slice(limit).forEach(({ index }) => {
            rows[index] = rows[index] || message
        })
    }
    tooMany(
        'URL',
        WA_LIMITS.urlButtons,
        `WhatsApp allows at most ${WA_LIMITS.urlButtons} website (URL) buttons on a template. Remove one.`
    )
    tooMany(
        'PHONE_NUMBER',
        WA_LIMITS.phoneButtons,
        `WhatsApp allows at most ${WA_LIMITS.phoneButtons} phone number button on a template. Remove one.`
    )
    if (typed.length > WA_LIMITS.buttons)
        messages.push(
            `WhatsApp allows at most ${WA_LIMITS.buttons} buttons on a template. Remove one.`
        )
    return { rows, messages }
}

/**
 * What must be fixed in a WhatsApp draft before it is sent for review,
 * apart from the fields every channel checks (name, approver, category).
 *
 * @param {{draft: object, body: string, variables: string[]}} args the
 *     draft (emptyWhatsAppDraft's shape), the body as typed, and its
 *     {{variables}}
 * @returns {{messages: string[], header: string, buttonRows: Object<number, string>}}
 *     `messages` for the banner; `header` and `buttonRows` mark the fields
 */
export const whatsappDraftProblems = ({ draft, body, variables }) => {
    const messages = []
    let header = ''
    const text = String(body ?? '')
    if (!text.trim()) messages.push('Template body is required.')
    else if (text.length > WA_LIMITS.body)
        messages.push(
            `The body can be at most ${WA_LIMITS.body} characters (it has ${text.length}).`
        )
    if ((variables ?? []).some((v) => !(draft.examples?.[v] ?? '').trim()))
        messages.push(
            'Every {{variable}} needs an example value: WhatsApp requires them to review the template.'
        )
    const headerText = String(draft.headerText ?? '').trim()
    if (draft.headerType === 'TEXT' && !headerText)
        header = 'Write the header text, or set the header to None.'
    else if (
        draft.headerType === 'TEXT' &&
        headerText.length > WA_LIMITS.headerText
    )
        header = `The header text can be at most ${WA_LIMITS.headerText} characters.`
    else if (isMediaHeader(draft.headerType))
        header = draft.mediaFile
            ? mediaFileProblem(draft.headerType, draft.mediaFile)
            : `Choose the header ${
                  WA_MEDIA[draft.headerType].label
              }, or set the header to None.`
    if (header) messages.push(header)
    if (String(draft.footerText ?? '').trim().length > WA_LIMITS.footerText)
        messages.push(
            `The footer can be at most ${WA_LIMITS.footerText} characters.`
        )
    const buttons = buttonProblems(draft.buttons)
    messages.push(...buttons.messages)
    return { messages, header, buttonRows: buttons.rows }
}

/**
 * The `meta_template_data` the API registers with WhatsApp: the header, the
 * footer and the buttons, each only when there is one.
 *
 * A media header is sent as `header_type: 'MEDIA'` with its `media_type`:
 * the registration also accepts IMAGE, VIDEO or DOCUMENT there, but the send
 * path attaches the media only for MEDIA, so any other value registers a
 * template whose every send then fails. The file is not sent here: it is
 * uploaded first (POST /comms/v2/files/upload), and `media` is that upload's
 * stored path with the file's name and type.
 *
 * @param {object} draft emptyWhatsAppDraft's shape
 * @param {{s3Path: string, fileName: string, fileType: string}|null} media
 *     the uploaded header media, for a media header
 * @returns {object}
 */
export const buildMetaTemplateData = (draft, media = null) => {
    const headerText = String(draft.headerText ?? '').trim()
    let header = null
    if (draft.headerType === 'TEXT' && headerText)
        header = { header_type: 'TEXT', header_text: headerText }
    if (isMediaHeader(draft.headerType) && media?.s3Path)
        header = {
            header_type: 'MEDIA',
            media_type: WA_MEDIA[draft.headerType].mediaType,
            media_s3_path: media.s3Path,
            file_name: media.fileName,
            file_type: media.fileType,
        }
    const footerText = String(draft.footerText ?? '').trim()
    const buttons = whatsappButtons(draft.buttons)
    return {
        ...(header && { header }),
        ...(footerText && {
            footer: { footer_type: 'TEXT', footer_text: footerText },
        }),
        ...(buttons.length && { buttons }),
    }
}

/**
 * A stored header's format: TEXT, IMAGE, VIDEO, DOCUMENT, or '' for none.
 *
 * The same reading as the registration (`_normalized_header_format`): a
 * MEDIA header takes its `media_type` (IMAGE when missing), and PDF is
 * WhatsApp's DOCUMENT.
 */
export const headerFormat = (header) => {
    let format = String(header?.header_type ?? '').toUpperCase()
    if (format === 'MEDIA')
        format = String(header?.media_type || 'IMAGE').toUpperCase()
    if (format === 'PDF') format = 'DOCUMENT'
    return ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(format) ? format : ''
}

/** A button type as the preview draws it: url, phone, quick_reply, copy_code, or ''. */
const buttonKind = (type) => {
    const value = String(type ?? '').toLowerCase()
    if (value === 'url') return 'url'
    if (value === 'phone_number') return 'phone'
    if (
        value === 'quick_reply' ||
        value === 'custom' ||
        value.startsWith('custom_')
    )
        return 'quick_reply'
    if (value === 'otp' || value === 'copy_code') return 'copy_code'
    return ''
}

/**
 * A template's buttons as the preview draws them, from any stored shape.
 *
 * Production stores them three ways: a list of `{button_type, button_text,
 * button_data}` (most rows, and what the form sends), an object keyed by the
 * button kind (`{URL: {...}, PHONE_NUMBER: {...}, CUSTOM_1: {...}}`, rows
 * made through the old Meta path), or nothing. The old preview read every
 * shape as an object and crashed the whole app on a missing one. Buttons
 * with no type or text are left out, as the form leaves them out of its
 * payload.
 *
 * @param {object[]|object|undefined} buttons
 * @returns {{kind: string, text: string}[]}
 */
export const previewButtons = (buttons) => {
    let entries = []
    if (Array.isArray(buttons))
        entries = buttons.map((b) => [b?.button_type, b])
    else if (buttons && typeof buttons === 'object')
        entries = Object.entries(buttons).map(([key, b]) => [
            b?.button_type || key,
            b,
        ])
    return entries
        .map(([type, b]) => ({
            kind: buttonKind(type),
            text: String(b?.button_text ?? '').trim(),
        }))
        .filter((b) => b.kind && b.text)
}

/**
 * The parts of a saved WhatsApp template a preview shows, from
 * GET /comms/v2/template/{id}: header text or header media (the
 * `pre_signed_url` the API signs for it), the footer and the buttons.
 * `meta_template_data` is missing on many rows, which then show the body
 * alone.
 *
 * @param {{meta_template_data?: object, pre_signed_url?: string}} details
 * @returns {{headerText: string, media: object|null, footer: string, buttons: *}}
 */
export const templatePreviewParts = (details) => {
    const meta = details?.meta_template_data ?? {}
    const format = headerFormat(meta.header)
    return {
        headerText:
            format === 'TEXT' ? String(meta.header?.header_text ?? '') : '',
        media: isMediaHeader(format)
            ? {
                  kind: format,
                  url: details?.pre_signed_url ?? '',
                  fileName: meta.header?.file_name ?? '',
              }
            : null,
        footer:
            String(meta.footer?.footer_type ?? 'TEXT').toUpperCase() === 'NONE'
                ? ''
                : String(meta.footer?.footer_text ?? ''),
        buttons: meta.buttons,
    }
}

// The body length the approval check needs (the old app's rule).
export const APPROVAL_MIN_BODY = 10

/**
 * Why the approval probability cannot be checked yet, or '' when it can:
 * a body of at least 10 characters and an example for every {{variable}}.
 */
export const approvalCheckBlocker = ({ body, variables, examples }) => {
    if (String(body ?? '').trim().length < APPROVAL_MIN_BODY)
        return `Write at least ${APPROVAL_MIN_BODY} characters in the body to check it.`
    const missing = (variables ?? []).filter(
        (v) => !(examples?.[v] ?? '').trim()
    )
    if (missing.length)
        return `Fill in an example for ${missing
            .map((v) => `{{${v}}}`)
            .join(', ')} to check it.`
    return ''
}

/**
 * True when an approval probability answer is the service's own failure.
 * It answers 200 even then, with category `error` (the AI call failed) or
 * `analysis_error` (its answer could not be read), and a made-up score.
 */
export const isApprovalError = (result) =>
    ['error', 'analysis_error'].includes(
        String(result?.category ?? '').toLowerCase()
    ) || typeof result?.percentage !== 'number'

/**
 * True when a revised message answer is the service's own failure: category
 * `error` or `revision_error`, with the original message sent back.
 */
export const isRevisionError = (result) =>
    ['error', 'revision_error'].includes(
        String(result?.approval_category ?? '').toLowerCase()
    ) || !String(result?.revised_message ?? '').trim()

/**
 * How a rewrite changes the variables: those of `before` it drops, and
 * those it adds.
 *
 * @returns {{dropped: string[], added: string[]}}
 */
export const variableChanges = (before, after) => {
    const was = whatsappVariables(before)
    const now = whatsappVariables(after)
    return {
        dropped: was.filter((v) => !now.includes(v)),
        added: now.filter((v) => !was.includes(v)),
    }
}
